import { Suspense, useEffect, useMemo, useState } from 'react'
import { getCurrentScreen } from '@auth0/auth0-acul-js'

import ScreenFrame from './components/ScreenFrame'
import { ensureUniversalLoginContext } from './utils/helpers/universalLoginContext'
import { getScreenComponent, type ScreenName } from './utils/screen/screenLoader'

type ScreenConfig = {
  id: ScreenName
  label: string
  tagline: string
  frameTitle: string
  frameSubtitle: string
  frameClassName?: string
}

const DEV_SCREENS: ScreenConfig[] = [
  {
    id: 'login',
    label: 'Login',
    tagline: 'Email + password entry',
    frameTitle: 'Sign in to your workspace',
    frameSubtitle: 'Use your company email address to continue.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'login-id',
    label: 'Login (Identifier)',
    tagline: 'Collect the email or username first',
    frameTitle: 'Start signing in',
    frameSubtitle: 'Enter your work email to continue.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'login-password',
    label: 'Login (Password)',
    tagline: 'Collect password after identifier',
    frameTitle: 'Enter your password',
    frameSubtitle: 'We recognize your email—confirm with password.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'reset-password-request',
    label: 'Reset password request',
    tagline: 'Start password recovery flow',
    frameTitle: 'Reset your password',
    frameSubtitle: 'Submit your identifier to receive reset instructions.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'reset-password-email',
    label: 'Reset password email',
    tagline: 'Email confirmation step',
    frameTitle: 'Check your email',
    frameSubtitle: 'Inform the user that reset instructions were sent.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'reset-password',
    label: 'Reset password',
    tagline: 'Set new password form',
    frameTitle: 'Create a new password',
    frameSubtitle: 'Collect and confirm the replacement password.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'reset-password-success',
    label: 'Reset password success',
    tagline: 'Password reset completion',
    frameTitle: 'Password updated',
    frameSubtitle: 'Guide the user back to login after success.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'reset-password-error',
    label: 'Reset password error',
    tagline: 'Password reset failure state',
    frameTitle: 'Unable to reset password',
    frameSubtitle: 'Let the user retry or return to sign in.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'signup-id',
    label: 'Sign up (Identifier)',
    tagline: 'Collect signup identifiers first',
    frameTitle: 'Start creating your account',
    frameSubtitle: 'Tell us how to reach you to continue.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'signup-password',
    label: 'Sign up (Password)',
    tagline: 'Set password after identifiers',
    frameTitle: 'Secure your new account',
    frameSubtitle: 'Create a password to finish signing up.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'signup',
    label: 'Sign up',
    tagline: 'Self-serve workspace creation',
    frameTitle: 'Create your Auth0 account',
    frameSubtitle: 'Provision a new workspace in a few seconds.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'mfa-begin-enroll-options',
    label: 'MFA Enrollment',
    tagline: 'Choose a second factor to enroll',
    frameTitle: 'Set up two-factor authentication',
    frameSubtitle: 'Pick the MFA method you want to use.',
    frameClassName: 'max-w-5xl',
  },
  {
    id: 'mfa-otp-challenge',
    label: 'MFA Challenge',
    tagline: 'Enter the one-time code to verify',
    frameTitle: 'Verify your identity',
    frameSubtitle: 'Enter the code from your authenticator app.',
    frameClassName: 'max-w-5xl',
  },
]

const DEFAULT_SCREEN: ScreenName = 'login'

const ScreenRenderer = ({ screenId }: { screenId: ScreenName }) => {
  const Component = getScreenComponent(screenId)

  if (!Component) {
    return (
      <div className="py-24 text-center text-sm text-slate-400">
        Screen &ldquo;{screenId}&rdquo; is not implemented yet.
      </div>
    )
  }

  return (
    <Suspense fallback={<div className="py-24 text-center text-sm text-slate-400">Loading…</div>}>
      <Component />
    </Suspense>
  )
}

const DevPlayground = () => {
  const [activeScreenId, setActiveScreenId] = useState<ScreenName>(DEFAULT_SCREEN)
  const [isReady, setIsReady] = useState(false)
  const [screenError, setScreenError] = useState<string | null>(null)

  useEffect(() => {
    let isCancelled = false
    setScreenError(null)
    setIsReady(false)

    ;(async () => {
      try {
        await ensureUniversalLoginContext(activeScreenId)
        if (!isCancelled) {
          setIsReady(true)
        }
      } catch (error) {
        if (!isCancelled) {
          setScreenError(error instanceof Error ? error.message : String(error))
        }
      }
    })()

    return () => {
      isCancelled = true
    }
  }, [activeScreenId])

  const activeScreen = useMemo(
    () => DEV_SCREENS.find((screen) => screen.id === activeScreenId) ?? DEV_SCREENS[0],
    [activeScreenId],
  )

  return (
    <div className="min-h-screen bg-slate-950 px-6 py-10 text-slate-100">
      <div className="mx-auto flex max-w-5xl flex-col gap-8">
        <header className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-[0.45em] text-slate-500">
            Auth0 / Custom Login
          </p>
        </header>

        <div className="flex flex-wrap gap-3">
          {DEV_SCREENS.map((screen) => {
            const isActive = screen.id === activeScreenId
            return (
              <button
                key={screen.id}
                type="button"
                onClick={() => setActiveScreenId(screen.id)}
                aria-pressed={isActive}
                className={
                  'flex min-w-[180px] flex-1 flex-col rounded-none border px-4 py-3 text-left transition sm:max-w-xs ' +
                  (isActive
                    ? 'border-white/80 bg-white/10 shadow-[0_25px_65px_rgba(15,23,42,0.45)]'
                    : 'border-slate-800 bg-slate-900/40 hover:border-white/30 hover:bg-white/5')
                }
              >
                <span className="text-sm font-semibold text-white">{screen.label}</span>
                <span className="text-xs text-slate-400">{screen.tagline}</span>
              </button>
            )
          })}
        </div>

        <ScreenFrame
          title={activeScreen.frameTitle}
          subtitle={activeScreen.frameSubtitle}
          className={activeScreen.frameClassName}
        >
          {screenError ? (
            <div className="rounded-none border border-rose-400/40 bg-rose-500/10 px-4 py-6 text-sm text-rose-200">
              <p className="font-semibold">Unable to load screen context</p>
              <p className="mt-2 opacity-90">{screenError}</p>
            </div>
          ) : isReady ? (
            <ScreenRenderer screenId={activeScreenId} />
          ) : (
            <div className="py-24 text-center text-sm text-slate-400">
              Loading {activeScreen.label} context...
            </div>
          )}
        </ScreenFrame>
      </div>
    </div>
  )
}

const ProductionRenderer = () => {
  const [screenId, setScreenId] = useState<ScreenName | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      const current = getCurrentScreen() || window.universal_login_context?.screen?.name
      if (!current) {
        throw new Error(
          'Universal Login Context is missing. Ensure `window.universal_login_context` is provided by the host page.',
        )
      }

      const component = getScreenComponent(current as ScreenName)
      if (!component) {
        throw new Error(`Screen "${current}" is not implemented in this bundle.`)
      }

      setScreenId(current as ScreenName)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [])

  if (error) {
    return (
      <div className="py-10 text-center text-sm text-rose-600">
        <p className="font-semibold">Unable to initialize screen</p>
        <p className="mt-1">{error}</p>
      </div>
    )
  }

  if (!screenId) {
    return <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
  }

  return <ScreenRenderer screenId={screenId} />
}

function App() {
  if (import.meta.env.DEV) {
    return <DevPlayground />
  }

  return <ProductionRenderer />
}

export default App
